// Register a hub: a WarmHub repository becomes a hub, with a toolset
// repository to push to. The new hub shows at once, before it has a toolset,
// on its Agent activity page, where its curator connects its hub agent.
// See Design > Exchange, "Curator API", and Design > Flows, "Toolset flow".

import { useState } from "preact/hooks";
import { Button, DetailLayout, Field, List, ListRow, Notice, PageHeader, RailPanel, TextInput } from "../../../../ui/src/index.ts";
import { listingView, refusalWords } from "../../domain/toolsets.ts";
import type { HubRecord } from "../../domain/types.ts";
import { Section } from "../parts.tsx";
import { register } from "../actions.ts";
import { hubLabel } from "../hubData.ts";
import { href, navigate } from "../router.ts";
import { useStore } from "../store.ts";

export function Register() {
  const [repo, setRepo] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const valid = /^[^/\s]+\/[^/\s]+$/.test(repo.trim());

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setProblem(null);
    const r = await register(repo.trim());
    setBusy(false);
    if (r.ok) navigate({ name: "agent", hub: r.value.hub.id });
    else setProblem(refusalWords("register", r.error));
  };

  return <>
    <PageHeader title={<>Register a <strong>hub</strong></>} description="Turn a WarmHub repository into a hub that answers questions from what it holds." />
    <DetailLayout rail={<RailPanel title="What happens">
      <p>Studio creates the hub and its toolset repository, a copy of the toolset template, and takes you to your hub agent.</p>
      <p>Connect your hub agent there, and it drafts the questions your hub will answer for you to review, then builds, tests and stages a toolset. You activate it. Or push a toolset yourself from the hub's Toolsets page.</p>
      <p class="st-rail-note">You need configure access to the repository you register. The hub reads that repository, and any others its toolset names, with access you grant.</p>
    </RailPanel>}>
      <form class="st-form" onSubmit={(e) => void submit(e)}>
        <Field id="register-repo" label="WarmHub repository" hint="As org/name, such as acme/field-notes.">{(attrs) =>
          <TextInput {...attrs} value={repo} placeholder="org/name" autocomplete="off" spellcheck={false} onInput={(e) => setRepo(e.currentTarget.value)} />}</Field>
        <div class="st-actions"><Button type="submit" variant="solid" disabled={!valid || busy}>{busy ? "Registering…" : "Register hub"}</Button>
          <Button href={href({ name: "home" })} variant="ghost">Cancel</Button></div>
        {problem && <Notice live tone="danger" title="Studio couldn't register that hub">{problem}</Notice>}
      </form>
    </DetailLayout>
  </>;
}

/** Hubs registered but without a toolset, which have only their Toolsets page. */
export function NewHubs({ records }: { records: readonly HubRecord[] }) {
  const hubs = useStore((s) => s.hubs);
  return <Section title="Hubs waiting for a first toolset" note="Push a toolset to each, test it and activate it. Its questions, earnings and demand appear after that.">
    <List>{records.map((r) => <ListRow key={r.id} name={hubLabel(r.name, hubs)} sub={listingView(r).label} href={href({ name: "toolsets", hub: r.id })} />)}</List>
  </Section>;
}
